import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Eraser,
  Loader2,
  Trash2,
} from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { sanitizeRichTextHtml } from '@/shared/lib/rich-text-html'
import { TaskNotesPanel } from '@/features/task-notes/TaskNotesPanel'
import { CommentsPanel } from '@/features/comments/CommentsPanel'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { Separator } from '@/shared/ui/separator'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { t } from '@/shared/lib/i18n'
import { formatNameWithId } from '@/shared/lib/formatNameWithId'
import { outlineButtonClassFor } from '@/shared/constants/statusPalette'
import { getTaskLogStatusDisplay } from '@/shared/lib/taskLogStatusDisplay'
import { showErrorToast } from '@/shared/lib/toast'
import apiClient from '@/shared/lib/apiClient'
import {
  instrumentReportsTaskDayOccurrencesPath,
  instrumentReportsTaskLogDetailPath,
  instrumentTaskTaskLogPath,
  nonComplianceResolveLogPath,
  nonComplianceReviewPath,
} from '@/shared/lib/routes'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import {
  ExplanationForm,
  ResolveForm,
  type DataFieldEntry,
  type DataFieldFile,
} from '@/features/non-compliance-actions'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'
import type {
  WebInstrumentTaskLogDetail,
  WebInstrumentsTaskDayOccurrence,
} from '@/shared/types/web'
import { FrequencyContextPanel } from '@/shared/ui/frequency-context-panel'
import { LogDetailBody } from './LogDetailsDialog'
import { CommentLoggingContextProvider, useCommentLoggingContext } from './commentLoggingContext'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

// ── Types ────────────────────────────────────────────────────────────────────

export type OccurrenceState = 'failure' | 'success' | 'no_testing' | 'override' | 'confirmed'

export type DayOccurrence = Omit<WebInstrumentsTaskDayOccurrence, 'state'> & {
  state: OccurrenceState
}

export interface TaskDayOccurrencesModalState {
  /** null = cross-task (day strip navigation spans all of the instrument's tasks). */
  instrumentTaskId: number | null
  taskName: string
  taskNotes: string | null
  date: string
  intervalTypeDescription: string | null
  dataFields: WebInstrumentsTaskLogDataField[]
}

interface Props {
  instrumentId: number
  instrumentLabel: string
  serialNumber: string | null
  modalState: TaskDayOccurrencesModalState | null
  onClose: () => void
  /**
   * Map of ISO date -> a representative id for that date. Powers the day strip
   * shown above the occurrence list so the user can page between dates without closing.
   * Should be scoped to the open task when instrumentTaskId is set.
   */
  dateToLogIdMap?: Record<string, number>
  /**
   * Optional calendar month bounds — forwarded as placement_from/to so cross-day
   * SUCCESS Completes whose completed_at falls outside the viewed month stay on RC.date.
   */
  placementFromDate?: string | null
  placementToDate?: string | null
  /** Refreshes the parent calendar after a successful review action. */
  onReviewSuccess?: () => void
}

type OccurrenceActionMode = 'none' | 'resolve' | 'ignore' | 'confirm'

// ── Status labels ─────────────────────────────────────────────────────────────

// A 'failure' state covers two distinct situations that must NOT read the same way in the
// header (REBUILD-330, Andrey Fedorov): an OPEN MISS (no execution log, requiredComplianceId
// set) is a true non-compliance and reads "Missed"; an EXECUTED FAILURE (the task was
// performed but its QC failed — an instrumentTaskLogId is present) is not a miss and must
// read "Failure". Legacy keeps executed failures out of the non-compliance surface entirely;
// here they stay in the merged Occurrences dialog but must never be labelled "Missed".
const isExecutedFailure = (occurrence: DayOccurrence): boolean =>
  occurrence.state === 'failure' && occurrence.instrumentTaskLogId != null

// Success Complete / Complete (Date Modified) labels + classes come from shared helper.
const successStatusDisplay = (occurrence: DayOccurrence) =>
  getTaskLogStatusDisplay({
    isNpt: false,
    qcPassed: true,
    dateModified: occurrence.dateModified === true,
  })

// No Testing / No Testing (Date Modified) labels + classes come from shared helper.
const nptStatusDisplay = (occurrence: DayOccurrence) =>
  getTaskLogStatusDisplay({
    isNpt: true,
    qcPassed: false,
    dateModified: occurrence.dateModified === true,
  })

export const statusLabel = (occurrence: DayOccurrence): string => {
  switch (occurrence.state) {
    case 'failure':
      return isExecutedFailure(occurrence)
        ? t('instruments.reports.dayOccurrences.statusExecutedFailure')
        : t('instruments.reports.dayOccurrences.statusFailure')
    case 'success':
      return successStatusDisplay(occurrence).label
    case 'no_testing':
      return nptStatusDisplay(occurrence).label
    case 'override':
      return t('instruments.reports.dayOccurrences.statusOverride')
    case 'confirmed':
      return t('instruments.reports.dayOccurrences.statusConfirmed')
  }
}

const NON_SUCCESS_STATUS_BADGE_CLASS: Record<
  Exclude<OccurrenceState, 'success' | 'no_testing'>,
  string
> = {
  failure: 'border-red-200 bg-red-100 text-red-700',
  override: 'border-teal-200 bg-teal-50 text-teal-700',
  confirmed: 'border-red-300 bg-red-50 text-red-600',
}

export const badgeClass = (occurrence: DayOccurrence): string => {
  if (isExecutedFailure(occurrence)) {
    return 'border-orange-200 bg-orange-100 text-orange-700'
  }
  if (occurrence.state === 'success') {
    return successStatusDisplay(occurrence).className
  }
  if (occurrence.state === 'no_testing') {
    return nptStatusDisplay(occurrence).className
  }
  return NON_SUCCESS_STATUS_BADGE_CLASS[occurrence.state]
}

interface OccurrenceStatusBadgeProps {
  occurrence: DayOccurrence
}

/** Row header status pill (also exported so unit tests can assert the rendered Badge). */
export const OccurrenceStatusBadge = ({ occurrence }: OccurrenceStatusBadgeProps) => (
  <Badge
    variant="outline"
    data-testid="day-occurrence-status"
    className={`text-xs ${badgeClass(occurrence)}`}
  >
    {statusLabel(occurrence)}
  </Badge>
)

const dayOfMonth = (isoDate: string): string => {
  const day = Number(isoDate.split('-')[2])
  return String(day).padStart(2, '0')
}

// Completed/NT/executed-fail rows carry the completing user. A confirmed miss has no
// backing log, so surface the reviewer who confirmed it (same "Name (ID:n)" placement).
const headerActorLabel = (occurrence: DayOccurrence): string | null => {
  const completedLabel = formatNameWithId(occurrence.completedByName, occurrence.completedByUserId)
  if (completedLabel != null) return completedLabel
  if (occurrence.state === 'confirmed') {
    return formatNameWithId(occurrence.reviewActorName, occurrence.reviewActorUserId)
  }
  return null
}

// Pulls a human-readable message out of a failed void response. The backend returns
// { errors: { base: ["..."], <field>: ["..."] } } for guard failures (e.g. backdate past a
// department move -> 422); fall back to any first error string, then a generic message.
const extractVoidError = (error: unknown): string => {
  const data = (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response
    ?.data
  const errors = data?.errors
  if (errors) {
    const firstMessage = errors.base?.[0] ?? Object.values(errors).flat().find(Boolean)
    if (firstMessage) return firstMessage
  }
  return t('instruments.reports.logDetails.error')
}

// AbortController + Axios cancel: prefer signal, also accept ERR_CANCELED when the
// runtime marks the rejection before signal.aborted is observed in the catch path.
const isAbortError = (controller: AbortController, error?: unknown): boolean => {
  if (controller.signal.aborted) return true
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: string }).code === 'ERR_CANCELED'
  )
}

// ── OccurrenceReadOnlyDetail ──────────────────────────────────────────────────
// Reuses the LogDetailsDialog body for already-completed / NT / executed-fail rows.

interface OccurrenceReadOnlyDetailProps {
  instrumentId: number
  instrumentTaskId: number
  instrumentTaskLogId: number
  canDeleteTaskLog?: boolean
  onDeleteLog?: (logId: number) => void
}

const OccurrenceReadOnlyDetail = ({
  instrumentId,
  instrumentTaskId,
  instrumentTaskLogId,
  canDeleteTaskLog = false,
  onDeleteLog,
}: OccurrenceReadOnlyDetailProps) => {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'loaded'; detail: WebInstrumentTaskLogDetail }
  >({ status: 'loading' })
  // Legacy parity (REBUILD-347, Andrey Fedorov): comments must be leavable on ANY completed
  // occurrence (compliance / No Testing / confirmed), not only on open-miss failures. The
  // occurrences dialog previously rendered these log-backed rows fully read-only.
  const [showComments, setShowComments] = useState(false)
  // REBUILD-498: gate the Add-Comment affordance on COMMENTS/create so the "User" role
  // cannot open an empty comment form on a completed occurrence.
  const canCreateComment = useCan(Resources.COMMENTS, Actions.CREATE)
  const detailAbortRef = useRef<AbortController | null>(null)

  // Reset to loading during render when the expanded row's log id changes so a stale
  // detail cannot flash under the new occurrence (mirrors dialog openIdentity sync).
  const [syncedLogId, setSyncedLogId] = useState(instrumentTaskLogId)
  if (syncedLogId !== instrumentTaskLogId) {
    setSyncedLogId(instrumentTaskLogId)
    setState({ status: 'loading' })
    setShowComments(false)
  }

  // Abort in-flight detail before paint when log id changes — useEffect cleanup alone
  // can let Log A's settled response paint under Log B after the render sync above.
  useLayoutEffect(() => {
    return () => {
      detailAbortRef.current?.abort()
    }
  }, [instrumentTaskLogId])

  const loadDetail = useCallback(() => {
    detailAbortRef.current?.abort()
    const controller = new AbortController()
    detailAbortRef.current = controller
    apiClient
      .get<{ reportsTaskLogDetail: WebInstrumentTaskLogDetail }>(
        instrumentReportsTaskLogDetailPath(instrumentId, instrumentTaskLogId, {}),
        { signal: controller.signal },
      )
      .then((res) => {
        if (isAbortError(controller)) return
        setState({ status: 'loaded', detail: res.data.reportsTaskLogDetail })
      })
      .catch((error: unknown) => {
        if (isAbortError(controller, error)) return
        setState({ status: 'error' })
      })
  }, [instrumentId, instrumentTaskLogId])

  useEffect(() => {
    loadDetail()
    return () => detailAbortRef.current?.abort()
  }, [loadDetail])

  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <p className="py-2 text-xs text-destructive">{t('instruments.reports.logDetails.error')}</p>
    )
  }

  return (
    <div className="space-y-2">
      <LogDetailBody
        detail={state.detail}
        instrumentId={instrumentId}
        instrumentTaskId={instrumentTaskId}
        canDeleteTaskLog={canDeleteTaskLog}
        onDeleteLog={onDeleteLog}
        showComments={showComments}
        onCommentChange={() => {
          setState({ status: 'loading' })
          loadDetail()
          setShowComments(false)
        }}
        hideIdentityRow
        hideStatusBadge
      />
      {!showComments && canCreateComment && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setShowComments(true)}>
            {t('instruments.reports.logDetails.addComment')}
          </Button>
        </div>
      )}
    </div>
  )
}

// ── ReviewExplanation ─────────────────────────────────────────────────────────

interface ReviewExplanationProps {
  comment: string
  actorName: string | null
  recordedAt: string | null
  hideAttribution?: boolean
}

const ReviewExplanation = ({
  comment,
  actorName,
  recordedAt,
  hideAttribution = false,
}: ReviewExplanationProps) => {
  const formattedDate = recordedAt ? formatCalendarDate(recordedAt) : null

  const attribution =
    !hideAttribution && actorName && formattedDate
      ? t('instruments.reports.dayOccurrences.reviewedBy')
          .replace('{actor}', actorName)
          .replace('{date}', formattedDate)
      : null

  return (
    <div className="rounded border border-muted bg-muted/30 px-3 py-2 text-xs">
      <p className="mb-1 font-medium text-muted-foreground">
        {t('instruments.reports.dayOccurrences.explanationLabel')}
      </p>
      <div
        className="rich-text-content prose prose-xs max-w-none text-foreground [&_p]:my-0"
        dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(comment) }}
      />
      {attribution && <p className="mt-1 text-muted-foreground">{attribution}</p>}
    </div>
  )
}

// ── OccurrenceDiscussionComments ──────────────────────────────────────────────
// Comments attached to the RequiredCompliance row itself (no backing log). Legacy parity
// (REBUILD-347): the calendar lets users comment on ANY occurrence, including a
// miss/override/confirmed row with no InstrumentTaskLog — legacy attaches these to
// `reportCompliance`. Rendered for the non-log branches below.

interface OccurrenceDiscussionCommentsProps {
  requiredComplianceId: number
  comments: DayOccurrence['comments']
  onCommentChange: () => void
}

const OccurrenceDiscussionComments = ({
  requiredComplianceId,
  comments,
  onCommentChange,
}: OccurrenceDiscussionCommentsProps) => {
  const [showForm, setShowForm] = useState(false)
  const commentLogging = useCommentLoggingContext()
  // REBUILD-498: gate the Add-Comment affordance on COMMENTS/create so the "User" role
  // cannot open an empty comment form on a required-compliance occurrence.
  const canCreateComment = useCan(Resources.COMMENTS, Actions.CREATE)

  return (
    <div className="space-y-2">
      {(comments.length > 0 || showForm) && (
        <CommentsPanel
          comments={comments}
          commentableType="RequiredCompliance"
          commentableId={requiredComplianceId}
          logAuthenticationCode={commentLogging.logAuthenticationCode}
          loggerOptions={commentLogging.loggerOptions}
          instrumentId={commentLogging.instrumentId}
          onCommentChange={() => {
            onCommentChange()
            setShowForm(false)
          }}
          compact
          hideTitle
          hideForm={!showForm}
        />
      )}
      {!showForm && canCreateComment && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
            {t('instruments.reports.logDetails.addComment')}
          </Button>
        </div>
      )}
    </div>
  )
}

// ── OccurrenceRow ─────────────────────────────────────────────────────────────

interface OccurrenceRowProps {
  occurrence: DayOccurrence
  instrumentId: number
  instrumentTaskId: number
  taskNotes: string | null
  date: string
  dataFields: WebInstrumentsTaskLogDataField[]
  onActionComplete: () => void
  /** When true, the row starts expanded (used for single-occurrence direct-open). */
  autoExpand?: boolean
  /** RBAC gate for the void action on log-backed rows. */
  canDeleteTaskLog?: boolean
  /** Opens the void-reason dialog for the given task log id. */
  onVoidLog?: (logId: number) => void
}

const OccurrenceRow = ({
  occurrence,
  instrumentId,
  instrumentTaskId,
  taskNotes,
  date,
  dataFields,
  onActionComplete,
  autoExpand = false,
  canDeleteTaskLog = false,
  onVoidLog,
}: OccurrenceRowProps) => {
  const { formatWallClockDisplay } = useSiteDateTime()
  const canReview = useCan(Resources.COMPLIANCE_REPORT, Actions.UPDATE)
  const [expanded, setExpanded] = useState(autoExpand)
  const [mode, setMode] = useState<OccurrenceActionMode>('none')
  const [processing, setProcessing] = useState(false)
  const actionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (autoExpand && actionsRef.current) {
      const firstButton =
        actionsRef.current.querySelector<HTMLButtonElement>('button:not(:disabled)')
      firstButton?.focus()
    }
  }, [autoExpand])

  // missedTime and windowStartTime are site-local wall-clock strings ("YYYY-MM-DDTHH:mm")
  // from the backend; ResolveForm uses them as-is for the picker prefill and guard checks.
  const siteTimeZone = occurrence.siteTimeZone ?? undefined
  const missedTime = occurrence.missedTime
  const windowStartTime = occurrence.windowStartTime

  // An actionable open miss is a `failure` RequiredCompliance row with NO backing log — the
  // scheduled window was never satisfied, so Resolve/NPT/Ignore/Confirm apply. A `failure` that
  // DOES carry a log is an executed-but-QC-failed run (legacy renders it as a red Failure cell;
  // the compliance engine excludes qc_failed logs so it never becomes a RequiredCompliance row —
  // it arrives here as a log-only occurrence with requiredComplianceId == null). Those actions all
  // require a requiredComplianceId and would silently no-op, so such a row must render read-only.
  const isUnreviewedMiss =
    occurrence.state === 'failure' &&
    occurrence.requiredComplianceId != null &&
    occurrence.instrumentTaskLogId == null
  const alreadyReviewed = occurrence.state === 'override' || occurrence.state === 'confirmed'
  const hasReadOnlyDetail = !isUnreviewedMiss && occurrence.instrumentTaskLogId != null

  const showReviewError = (errors: Record<string, string>) => {
    const message =
      errors['base'] ??
      errors['comments'] ??
      Object.values(errors)[0] ??
      t('nonCompliance.errors.reviewFailed')
    showErrorToast(message)
  }

  const handleNpt = () => {
    if (processing) return
    if (occurrence.requiredComplianceId == null) return
    router.post(
      nonComplianceReviewPath(occurrence.requiredComplianceId),
      { review_status_code: 'NPT' },
      {
        preserveState: true,
        onStart: () => setProcessing(true),
        onSuccess: onActionComplete,
        onError: (errors) => showReviewError(errors),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const postReview = (reviewStatusCode: string, comments: string) => {
    if (occurrence.requiredComplianceId == null) return
    router.post(
      nonComplianceReviewPath(occurrence.requiredComplianceId),
      { review_status_code: reviewStatusCode, comments },
      {
        preserveState: true,
        onStart: () => setProcessing(true),
        onSuccess: onActionComplete,
        onError: (errors) => showReviewError(errors),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const handleResolveSubmit = (
    completedAt: string,
    comments: string,
    dataFieldEntries: DataFieldEntry[],
    dataFieldFiles: DataFieldFile[],
  ) => {
    if (occurrence.requiredComplianceId == null) return
    router.post(
      nonComplianceResolveLogPath(occurrence.requiredComplianceId),
      {
        completed_at: completedAt,
        comments,
        data_field_entries: dataFieldEntries,
        data_field_files: dataFieldFiles,
      },
      {
        preserveState: true,
        onStart: () => setProcessing(true),
        onSuccess: onActionComplete,
        onError: (errors) => showReviewError(errors),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const actionsDisabled = !canReview || processing

  return (
    <div className="rounded-md border">
      {/* Row header — always visible, toggles expanded */}
      <button
        type="button"
        className="flex w-full items-center gap-2 p-2 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded((prev) => !prev)
          if (!expanded) setMode('none')
        }}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <OccurrenceStatusBadge occurrence={occurrence} />
        <span className="text-xs text-muted-foreground">
          {occurrence.occurrenceTime
            ? [
                headerActorLabel(occurrence),
                // REBUILD-474: occurrence / missed header uses MM/DD/YYYY, HH:mm.
                formatWallClockDisplay(occurrence.occurrenceTime),
              ]
                .filter(Boolean)
                .join(' ')
            : '—'}
        </span>
      </button>

      {/* Expanded detail / actions */}
      {expanded && (
        <div className="border-t p-3 text-sm space-y-3">
          {isUnreviewedMiss &&
            (occurrence.intervalTypeDescription || occurrence.frequencyTypeCode) && (
              <FrequencyContextPanel
                frequencyTypeCode={occurrence.frequencyTypeCode}
                intervalTypeDescription={occurrence.intervalTypeDescription}
                frequencyRrule={occurrence.frequencyRrule}
                leadTimeIntervalHours={occurrence.leadTimeHours}
                leadTimeUnit={occurrence.leadTimeUnit}
                lateTimeIntervalHours={occurrence.overdueHours}
                lateTimeUnit={occurrence.lateTimeUnit}
                fixedScheduleStartTime={occurrence.fixedScheduleStartTime}
                endsAtMidnight={occurrence.endsAtMidnight}
              />
            )}

          {isUnreviewedMiss && mode === 'none' && (
            <>
              {!canReview && <NoAccessNotice />}
              <div ref={actionsRef} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <ActionCard
                  icon={<ClipboardList className="h-5 w-5" />}
                  title={t('nonCompliance.actions.resolve.title')}
                  subtitle={t('nonCompliance.actions.resolve.subtitle')}
                  colorClass={outlineButtonClassFor('blue', true)}
                  onClick={() => setMode('resolve')}
                  disabled={actionsDisabled}
                />
                <ActionCard
                  icon={<Eraser className="h-5 w-5" />}
                  title={t('nonCompliance.actions.npt.title')}
                  subtitle={t('nonCompliance.actions.npt.subtitle')}
                  colorClass={outlineButtonClassFor('grey')}
                  onClick={handleNpt}
                  disabled={actionsDisabled}
                />
                <ActionCard
                  icon={<Trash2 className="h-5 w-5" />}
                  title={t('nonCompliance.actions.ignore.title')}
                  subtitle={t('nonCompliance.actions.ignore.subtitle')}
                  colorClass={outlineButtonClassFor('yellow')}
                  onClick={() => setMode('ignore')}
                  disabled={actionsDisabled}
                />
                <ActionCard
                  icon={<AlertTriangle className="h-5 w-5" />}
                  title={t('nonCompliance.actions.confirm.title')}
                  subtitle={t('nonCompliance.actions.confirm.subtitle')}
                  colorClass={outlineButtonClassFor('red')}
                  onClick={() => setMode('confirm')}
                  disabled={actionsDisabled}
                />
              </div>
            </>
          )}

          {isUnreviewedMiss && mode === 'resolve' && (
            <ResolveForm
              date={date}
              missedTime={missedTime}
              windowStartTime={windowStartTime}
              siteTimeZone={siteTimeZone}
              taskNotes={taskNotes}
              hideTaskNotes
              dataFields={dataFields}
              onSubmit={handleResolveSubmit}
              onCancel={() => setMode('none')}
              processing={processing}
            />
          )}

          {isUnreviewedMiss && mode === 'ignore' && (
            <ExplanationForm
              warningPrimary={t('nonCompliance.dialogs.ignore.warningPrimary')}
              commentsPlaceholder={t('nonCompliance.dialogs.ignore.commentsPlaceholder')}
              submitLabel={t('nonCompliance.dialogs.ignore.submit')}
              cancelLabel={t('nonCompliance.dialogs.ignore.cancel')}
              onSubmit={(comments) => postReview('ODE', comments)}
              onCancel={() => setMode('none')}
              processing={processing}
            />
          )}

          {isUnreviewedMiss && mode === 'confirm' && (
            <ExplanationForm
              warningPrimary={t('nonCompliance.dialogs.confirm.warningPrimary')}
              warningSecondary={t('nonCompliance.dialogs.confirm.warningSecondary')}
              commentsPlaceholder={t('nonCompliance.dialogs.confirm.commentsPlaceholder')}
              submitLabel={t('nonCompliance.dialogs.confirm.submit')}
              cancelLabel={t('nonCompliance.dialogs.confirm.cancel')}
              onSubmit={(comments) => postReview('CNM', comments)}
              onCancel={() => setMode('none')}
              processing={processing}
            />
          )}

          {/* Read-only detail for completed / NT / executed-fail rows */}
          {hasReadOnlyDetail && (
            <div className="space-y-2">
              <OccurrenceReadOnlyDetail
                instrumentId={instrumentId}
                instrumentTaskId={occurrence.instrumentTaskId ?? instrumentTaskId}
                instrumentTaskLogId={occurrence.instrumentTaskLogId!}
                canDeleteTaskLog={canDeleteTaskLog && !alreadyReviewed}
                onDeleteLog={onVoidLog}
              />
              {alreadyReviewed && occurrence.reviewComment && (
                <ReviewExplanation
                  comment={occurrence.reviewComment}
                  actorName={occurrence.reviewActorName}
                  recordedAt={occurrence.reviewRecordedAt}
                />
              )}
            </div>
          )}

          {/* Non-miss rows with no backing log: reviewed rows vs completed/NT */}
          {!isUnreviewedMiss && !hasReadOnlyDetail && (
            <div className="space-y-2">
              {!alreadyReviewed && (
                <div className="rounded border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-800">
                  {t('instruments.reports.dayOccurrences.completedNoDetail')}
                </div>
              )}

              {occurrence.state === 'confirmed' ? (
                <>
                  {occurrence.reviewRecordedAt && (
                    <p className="text-sm text-foreground">
                      <span className="font-semibold">
                        {t('instruments.reports.dayOccurrences.confirmedAt')}
                      </span>{' '}
                      {formatWallClockDisplay(occurrence.reviewRecordedAt)}
                    </p>
                  )}
                  {(occurrence.intervalTypeDescription || occurrence.frequencyTypeCode) && (
                    <FrequencyContextPanel
                      frequencyTypeCode={occurrence.frequencyTypeCode}
                      intervalTypeDescription={occurrence.intervalTypeDescription}
                      frequencyRrule={occurrence.frequencyRrule}
                      leadTimeIntervalHours={occurrence.leadTimeHours}
                      leadTimeUnit={occurrence.leadTimeUnit}
                      lateTimeIntervalHours={occurrence.overdueHours}
                      lateTimeUnit={occurrence.lateTimeUnit}
                      fixedScheduleStartTime={occurrence.fixedScheduleStartTime}
                      endsAtMidnight={occurrence.endsAtMidnight}
                    />
                  )}
                  {occurrence.reviewComment && (
                    <ReviewExplanation
                      comment={occurrence.reviewComment}
                      actorName={occurrence.reviewActorName}
                      recordedAt={occurrence.reviewRecordedAt}
                      hideAttribution
                    />
                  )}
                </>
              ) : (
                <>
                  {(occurrence.intervalTypeDescription || occurrence.frequencyTypeCode) && (
                    <FrequencyContextPanel
                      frequencyTypeCode={occurrence.frequencyTypeCode}
                      intervalTypeDescription={occurrence.intervalTypeDescription}
                      frequencyRrule={occurrence.frequencyRrule}
                      leadTimeIntervalHours={occurrence.leadTimeHours}
                      leadTimeUnit={occurrence.leadTimeUnit}
                      lateTimeIntervalHours={occurrence.overdueHours}
                      lateTimeUnit={occurrence.lateTimeUnit}
                      fixedScheduleStartTime={occurrence.fixedScheduleStartTime}
                      endsAtMidnight={occurrence.endsAtMidnight}
                    />
                  )}
                  {alreadyReviewed && occurrence.reviewComment && (
                    <ReviewExplanation
                      comment={occurrence.reviewComment}
                      actorName={occurrence.reviewActorName}
                      recordedAt={occurrence.reviewRecordedAt}
                    />
                  )}
                </>
              )}

              {occurrence.requiredComplianceId != null && (
                <OccurrenceDiscussionComments
                  requiredComplianceId={occurrence.requiredComplianceId}
                  comments={occurrence.comments}
                  onCommentChange={onActionComplete}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── ActionCard ────────────────────────────────────────────────────────────────

interface ActionCardProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  colorClass: string
  onClick: () => void
  disabled?: boolean
}

const ActionCard = ({ icon, title, subtitle, colorClass, onClick, disabled }: ActionCardProps) => {
  const className = [
    'flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-colors cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    colorClass,
    disabled ? 'pointer-events-none opacity-50' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled}>
      {icon}
      <span className="text-xs font-semibold">{title}</span>
      <span className="text-xs opacity-75">{subtitle}</span>
    </button>
  )
}

// ── TaskDayOccurrencesDialog ──────────────────────────────────────────────────

type ListState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; occurrences: DayOccurrence[] }

const TaskDayOccurrencesDialog = ({
  instrumentId,
  instrumentLabel,
  serialNumber,
  modalState,
  onClose,
  dateToLogIdMap,
  placementFromDate,
  placementToDate,
  onReviewSuccess,
}: Props) => {
  const isOpen = modalState !== null
  const headerTitle = serialNumber ? `${instrumentLabel} (${serialNumber})` : instrumentLabel

  const canDeleteTaskLog = useCan(Resources.INSTRUMENT_TASK_LOG, Actions.DELETE)

  const [listState, setListState] = useState<ListState>({ status: 'loading' })
  // REBUILD-531: open-logging context for report comments on this day-occurrences surface.
  // Captured from the task_day_occurrences payload (sibling keys to the occurrences) so the
  // CommentsPanel/LogDetailBody add-comment flow offers logger selection + "Add new user" on
  // an OL instrument and stays hidden on an authenticated-users-only one.
  const [logAuthenticationCode, setLogAuthenticationCode] = useState<string | null>(null)
  const [loggerOptions, setLoggerOptions] = useState<{ id: number; label: string }[]>([])
  // The strip pages between dates without tearing down the dialog, so the visible date is
  // local state seeded from the clicked cell; falls back to the incoming modalState.date.
  const [currentDate, setCurrentDate] = useState<string | null>(modalState?.date ?? null)

  const [voidTargetLogId, setVoidTargetLogId] = useState<number | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [voiding, setVoiding] = useState(false)
  const [voidError, setVoidError] = useState<string | null>(null)
  const fetchAbortRef = useRef<AbortController | null>(null)

  const clearVoidUiState = () => {
    setVoidTargetLogId(null)
    setVoidReason('')
    setVoiding(false)
    setVoidError(null)
  }

  // Dialog stays mounted while closed. When a new cell opens, sync date + clear the prior
  // list during render so the first paint cannot show the previous day's title/rows (useEffect
  // alone would flash stale data). Strip clicks only change currentDate — openIdentity stays
  // keyed to the opened cell, so strip paging is unaffected. On close, clear list/date so a
  // reopen never briefly inherits prior rows.
  const openIdentity = modalState
    ? `${modalState.instrumentTaskId ?? 'all'}:${modalState.date}`
    : null
  const [syncedOpenIdentity, setSyncedOpenIdentity] = useState<string | null>(openIdentity)
  if (openIdentity !== syncedOpenIdentity) {
    setSyncedOpenIdentity(openIdentity)
    // Void AlertDialog state must not survive close/cell switch — otherwise a prior
    // voidTargetLogId can keep the confirm dialog open against a stale log id.
    clearVoidUiState()
    if (modalState) {
      setCurrentDate(modalState.date)
      setListState({ status: 'loading' })
    } else {
      setCurrentDate(null)
      setListState({ status: 'loading' })
    }
  }

  // Abort in-flight fetch before paint when the opened cell identity changes — the list
  // fetch effect has not run yet, so without this Day A can resolve under Day B's title.
  // (Must not touch refs during render; layout effect is the race-safe alternative.)
  useLayoutEffect(() => {
    return () => {
      fetchAbortRef.current?.abort()
    }
  }, [openIdentity])

  // Prefer strip-selected date; fall back to the opened cell date. Keep this derivation
  // resilient so post-action refresh and title never skip when the dialog is open.
  const activeDate = currentDate || modalState?.date || null

  const fetchOccurrences = useCallback(
    (targetDate: string) => {
      if (!modalState) return

      fetchAbortRef.current?.abort()
      const controller = new AbortController()
      fetchAbortRef.current = controller

      setListState({ status: 'loading' })
      apiClient
        .get<{
          taskDayOccurrences: DayOccurrence[]
          logAuthenticationCode: string | null
          loggerOptions: { id: number; label: string }[]
        }>(instrumentReportsTaskDayOccurrencesPath(instrumentId), {
          signal: controller.signal,
          params: {
            // Scope to the clicked cell's task so the list matches the task-specific
            // header. The day strip pages between dates without changing the task, so a
            // strip click re-fetches the same task's occurrences on the new date.
            // A cross-task open (day strip navigation) passes a null task id and sees all.
            // Day scope is `date` only — do not send calendar from_date/to_date as a row
            // filter (a window filter previously dropped compliance rows).
            // placement_from/to inform cross-day SUCCESS placement only (keep Complete on
            // RC.date when completed_at is outside the viewed month).
            instrument_task_id: modalState.instrumentTaskId,
            date: targetDate,
            ...(placementFromDate ? { placement_from_date: placementFromDate } : {}),
            ...(placementToDate ? { placement_to_date: placementToDate } : {}),
          },
        })
        .then(({ data }) => {
          if (isAbortError(controller)) return
          setListState({ status: 'loaded', occurrences: data.taskDayOccurrences })
          setLogAuthenticationCode(data.logAuthenticationCode ?? null)
          setLoggerOptions(data.loggerOptions ?? [])
        })
        .catch((error: unknown) => {
          if (isAbortError(controller, error)) return
          setListState({ status: 'error' })
        })
    },
    [instrumentId, modalState, placementFromDate, placementToDate],
  )

  useEffect(() => () => fetchAbortRef.current?.abort(), [])

  useEffect(() => {
    // Component stays mounted while closed — abort in-flight work on close so a late
    // response cannot write into listState after the user dismissed the dialog.
    if (!modalState) {
      fetchAbortRef.current?.abort()
      return
    }
    // Prefer strip-selected date when set so a placement-window change mid-strip does
    // not snap back to the originally opened cell date.
    fetchOccurrences(currentDate || modalState.date)
    // Abort when the opened cell identity or placement window changes so Day A cannot
    // paint under Day B, and a parent month change re-fetches with new placement bounds.
    return () => fetchAbortRef.current?.abort()
    // Strip clicks also call fetchOccurrences directly; currentDate is read for placement
    // re-fetch but strip navigation is owned by handleDateStripClick (avoids double-fetch
    // loops from listing currentDate as a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- strip uses handleDateStripClick; placementFrom/To included so month change re-fetches
  }, [modalState?.instrumentTaskId, modalState?.date, placementFromDate, placementToDate])

  // Refresh the parent calendar AND re-fetch the open list so the acted-on row
  // updates in place without tearing down the dialog (AC 5). Always refresh when the
  // dialog is open — do not gate solely on activeDate (empty/unset strip state must not
  // leave a stale list while the parent calendar refreshes).
  const handleActionComplete = () => {
    onReviewSuccess?.()
    if (!modalState) return
    fetchOccurrences(currentDate || modalState.date)
  }

  const handleDateStripClick = (stripDate: string) => {
    // Strip paging keeps openIdentity stable — clear void UI here so a confirm dialog
    // from day A cannot stay open over day B's list (openIdentity sync alone is not enough).
    clearVoidUiState()
    setCurrentDate(stripDate)
    fetchOccurrences(stripDate)
  }

  const handleVoidRequest = (logId: number) => {
    setVoidTargetLogId(logId)
    setVoidReason('')
    setVoidError(null)
  }

  const handleVoidCancel = () => {
    clearVoidUiState()
  }

  const finishVoid = () => {
    clearVoidUiState()
    handleActionComplete()
  }

  const handleVoidConfirm = (event: React.MouseEvent) => {
    // Radix AlertDialogAction closes the dialog on click by default; prevent that so a
    // failed void (e.g. 422 backdate guard) keeps the dialog open and shows the error.
    // finishVoid() closes it programmatically only on success/404.
    event.preventDefault()
    if (voidTargetLogId == null) return

    // Legacy parity (sweetAlertWithDeleteLog inputValidator): a reason is mandatory. Block the
    // submit and surface the validation message instead of dispatching an empty-reason void.
    const trimmedReason = voidReason.trim()
    if (trimmedReason.length === 0) {
      setVoidError(t('instruments.reports.logDetails.voidReasonRequired'))
      return
    }

    // The strip can surface a log belonging to a different task than the clicked cell, so
    // void against the occurrence's OWN task id (read from the loaded list) to hit the
    // correct destroy route — mirroring LogDetailsDialog.handleVoidConfirm.
    const occurrences = listState.status === 'loaded' ? listState.occurrences : []
    const targetTaskId = occurrences.find(
      (occurrence) => occurrence.instrumentTaskLogId === voidTargetLogId,
    )?.instrumentTaskId
    if (targetTaskId == null) {
      setVoidError(t('instruments.reports.logDetails.voidTaskIdMissing'))
      return
    }

    setVoiding(true)
    setVoidError(null)
    apiClient
      .delete(instrumentTaskTaskLogPath(instrumentId, targetTaskId), {
        data: { delete_reason: trimmedReason, instrument_task_log_id: voidTargetLogId },
      })
      .then(() => finishVoid())
      .catch((error) => {
        // A 404 means the log was already voided (double-submit or stale list). Treat it as
        // success and refresh so the stale row disappears instead of surfacing an error.
        if (error.response?.status === 404) {
          finishVoid()
          return
        }
        setVoiding(false)
        setVoidError(extractVoidError(error))
      })
  }

  const occurrences = listState.status === 'loaded' ? listState.occurrences : []
  const stripEntries = dateToLogIdMap
    ? Object.keys(dateToLogIdMap).sort((a, b) => a.localeCompare(b))
    : []

  return (
    <CommentLoggingContextProvider.Provider
      value={{ logAuthenticationCode, loggerOptions, instrumentId }}
    >
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          className="max-h-[90vh] max-w-lg overflow-y-auto"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-sm font-normal text-muted-foreground">
              {headerTitle}
            </DialogTitle>
            <p className="text-base font-semibold">
              {activeDate
                ? t('instruments.reports.dayOccurrences.dialogTitle', { date: activeDate })
                : ''}
            </p>
            {modalState && <p className="text-sm text-muted-foreground">{modalState.taskName}</p>}
          </DialogHeader>

          {/* Task notes — shared rich-text panel (REBUILD-377). Shown for the whole day view
              (any result: completed, NT, miss); hidden when empty. */}
          {modalState && <TaskNotesPanel notes={modalState.taskNotes} className="bg-muted/40" />}

          {/* Cross-task day strip — page between dates that have logs or misses */}
          {stripEntries.length > 0 && (
            <div className="flex flex-wrap gap-1 border-b pb-2">
              {stripEntries.map((stripDate) => {
                const isActive = activeDate === stripDate
                return (
                  <button
                    key={stripDate}
                    type="button"
                    className={[
                      'rounded px-1.5 py-0.5 text-xs transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground font-semibold'
                        : 'bg-muted text-muted-foreground hover:bg-muted/70',
                    ].join(' ')}
                    onClick={() => handleDateStripClick(stripDate)}
                    aria-label={t('instruments.reports.logDetails.dateStripAria', {
                      day: dayOfMonth(stripDate),
                    })}
                    aria-pressed={isActive}
                  >
                    {dayOfMonth(stripDate)}
                  </button>
                )
              })}
            </div>
          )}

          {modalState && (
            <div className="space-y-2 text-sm">
              {listState.status === 'loading' && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}

              {listState.status === 'error' && (
                <p className="py-4 text-center text-sm text-destructive">
                  {t('instruments.reports.dayOccurrences.error')}
                </p>
              )}

              {listState.status === 'loaded' && occurrences.length === 0 && (
                <p className="py-4 text-center text-muted-foreground">
                  {t('instruments.reports.dayOccurrences.empty')}
                </p>
              )}

              {listState.status === 'loaded' &&
                occurrences.map((occurrence, index) => (
                  <OccurrenceRow
                    key={
                      occurrence.requiredComplianceId ??
                      (occurrence.instrumentTaskLogId != null
                        ? `log-${occurrence.instrumentTaskLogId}`
                        : `occ-${occurrence.occurrenceTime ?? index}`)
                    }
                    occurrence={occurrence}
                    instrumentId={instrumentId}
                    instrumentTaskId={
                      occurrence.instrumentTaskId ?? modalState.instrumentTaskId ?? 0
                    }
                    taskNotes={modalState.taskNotes}
                    date={activeDate ?? modalState.date}
                    dataFields={modalState.dataFields}
                    onActionComplete={handleActionComplete}
                    autoExpand={occurrences.length === 1}
                    canDeleteTaskLog={canDeleteTaskLog}
                    onVoidLog={handleVoidRequest}
                  />
                ))}

              <Separator />

              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={onClose}>
                  {t('instruments.reports.logDetails.close')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Void confirmation dialog */}
      <AlertDialog
        open={voidTargetLogId != null}
        onOpenChange={(open) => !open && handleVoidCancel()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('instruments.reports.logDetails.voidDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('instruments.reports.logDetails.voidDialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="occurrence-void-reason">
              {t('instruments.reports.logDetails.voidReasonLabel')}
            </Label>
            <Textarea
              id="occurrence-void-reason"
              rows={3}
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              placeholder={t('instruments.reports.logDetails.voidReasonPlaceholder')}
              disabled={voiding}
            />
            {voidError && <p className="text-sm text-destructive">{voidError}</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={voiding}>
              {t('instruments.reports.logDetails.voidCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoidConfirm}
              disabled={voiding || voidReason.trim().length === 0}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {voiding
                ? t('instruments.reports.logDetails.voidSubmitting')
                : t('instruments.reports.logDetails.voidConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CommentLoggingContextProvider.Provider>
  )
}

export default TaskDayOccurrencesDialog
